/**
 * Mobile (below lg) view of the "Cut To" box: a dashed panel with Remove Cut and Edit links and
 * one label / value row per field. Chosen by `./DefineCutDisplay.tsx`, which supplies the fields,
 * labels and values. `components/Product/ProductDisplayDefineCut/ProductDisplayDefineCut.tsx`
 * renders this view at every width on the product page.
 */
import { useTheme } from "@ryerson/frontend.theme";
import React from "react";
import { DefineCutDisplayProps } from "./DefineCutDisplay";
import { Typography } from "@ryerson/frontend.typography";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { emDash } from "@ryerson/frontend.common";

interface DefinedCutContainerProps {
	borderColor: string;
	backgroundColor: string;
}

interface DividerProps {
	color: string;
}

const DefinedCutContainer = styled(Flex)<DefinedCutContainerProps>`
	margin: 0;
	border-radius: 2px;
	padding: 10px;
	align-items: center;

	${(props: DefinedCutContainerProps) => {
		const { borderColor, backgroundColor } = props;
		return css`
			border: 1px dashed ${borderColor};
			background-color: ${backgroundColor};
		`;
	}};
`;

const DefinedCutRow = styled(Flex)`
	width: 100%;
	padding: 10px;
`;

const Divider = styled.hr<DividerProps>`
	margin: 0px;
	opacity: 0.1;
	border-width: 1px 0 0 0;
	${(props: DividerProps) => {
		const { color } = props;
		return css`
			border-color: ${color};
		`;
	}};
`;

/** Renders nothing when `displayFields` is empty. Ignores `column`. */
const DefineCutDisplayMobile: React.FC<DefineCutDisplayProps> = ({
	content,
	displayFields,
	displayAttributeName,
	displayAttributeValue,
	onClickEdit,
	onClickDelete,
	canEditCut,
	canDeleteCut,
}) => {
	const { theme } = useTheme();
	const { link, text, secondaryBrand, white } = theme.colors.primary;

	const renderGridItem = (color: string, text: string | number, isProperty?: boolean) => {
		return (
			<Typography
				size="md"
				weight={isProperty ? "bold" : "normal"}
				color={color}
				css={{ width: "50%", textAlign: isProperty ? "unset" : "right" }}
			>
				{text}
			</Typography>
		);
	};

	return (
		<>
			{displayFields.length > 0 && (
				<DefinedCutContainer
					direction="column"
					justifyContent="space-between"
					{...{ borderColor: secondaryBrand, backgroundColor: white }}
				>
					<DefinedCutRow justifyContent="space-between">
						<Typography weight="bold" color={text}>
							{content.cutTo}:
						</Typography>
					</DefinedCutRow>
					{(canDeleteCut || canEditCut) && (
						<DefinedCutRow justifyContent="space-between">
							{canDeleteCut && (
								<FlexItem onClick={onClickDelete}>
									<Flex alignItems="center">
										<Icon
											icon={"trash"}
											color={link}
											size="sm"
											css={{ marginRight: 5 }}
										/>
										<Typography color={link}>{content.removeCut}</Typography>
									</Flex>
								</FlexItem>
							)}
							{canEditCut && (
								<FlexItem onClick={onClickEdit}>
									<Flex alignItems="center">
										<Icon
											icon={"edit"}
											color={link}
											size="sm"
											css={{ marginRight: 5 }}
										/>
										<Typography color={link}>{content.edit}</Typography>
									</Flex>
								</FlexItem>
							)}
						</DefinedCutRow>
					)}

					<DefinedCutRow direction="column" justifyContent="space-between">
						{displayFields.map((field: string) => {
							return (
								<div key={field}>
									<Divider color={secondaryBrand} />
									<Flex
										justifyContent="space-between"
										css={{ width: "100%", padding: "5px 0" }}
									>
										{renderGridItem(text, displayAttributeName(field), true)}
										{renderGridItem(
											text,
											displayAttributeValue(field) || emDash
										)}
									</Flex>
								</div>
							);
						})}
					</DefinedCutRow>
				</DefinedCutContainer>
			)}
		</>
	);
};

export default DefineCutDisplayMobile;
